import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Save, RotateCcw, ShieldCheck, Lock, Info } from "lucide-react";

/**
 * What each advertiser role may do (round 10).
 *
 * Six roles shipped with the team feature and exactly one restriction was ever
 * enforced. The matrix behind this page is now applied on every route; this is where
 * it gets changed without a deploy.
 *
 * A GRID rather than eleven toggle panels per role. Six by eleven is small enough to
 * read at once, and the question an admin actually has is comparative: "can Finance do
 * this when Marketing can?" A stack of panels answers that only by scrolling.
 *
 * OWNER's row is rendered and locked rather than hidden. Round 8's Tier Pricing lesson:
 * an admin who cannot tell which controls do nothing assumes the ones that do nothing
 * are broken. Showing the row with the reason on it is better than an unexplained gap.
 */
export default function RolePermissions() {
  const { token } = useAuth();
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/settings/role-config`, auth);
      setData(res.data);
      setDraft(res.data.roles || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the role matrix");
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const fixed = useMemo(() => new Set(data?.fixed_roles || []), [data]);

  // Compared against what the SERVER says is in force, not against the code defaults:
  // a role left at its default and a role explicitly set to the same thing are the
  // same state, and showing one as changed would be noise.
  const dirty = useMemo(() => {
    if (!data) return false;
    return Object.keys(data.roles || {}).some(
      (role) => (data.roles[role] || []).join() !== (draft[role] || []).join()
    );
  }, [data, draft]);

  const toggle = (role, permission) => {
    if (fixed.has(role)) return;
    setDraft((prev) => {
      const held = new Set(prev[role] || []);
      if (held.has(permission)) held.delete(permission);
      else held.add(permission);
      // Kept in PERMISSIONS order so the draft matches what the server returns and the
      // dirty comparison above stays a string compare.
      return { ...prev, [role]: (data.permissions || []).map((p) => p.key).filter((k) => held.has(k)) };
    });
  };

  const resetRole = (role) => {
    setDraft((prev) => ({ ...prev, [role]: [...(data.defaults?.[role] || [])] }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings/role-config`, { roles: draft }, auth);
      // Adopt the RESOLVED matrix the server returns rather than the draft: they differ
      // wherever a role is fixed, and the grid must show what is in force.
      setData((prev) => ({ ...prev, roles: res.data.roles }));
      setDraft(res.data.roles);
      toast.success("Role permissions saved. They apply on the next request.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the role matrix");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!data) return null;

  const roles = Object.keys(data.roles || {});
  const permissions = data.permissions || [];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight flex items-center gap-3">
            <ShieldCheck className="h-8 w-8 text-primary" />
            Role Permissions
          </h1>
          <p className="text-muted-foreground mt-1 max-w-3xl">
            What each role on an advertiser account may do. Applies to every account,
            takes effect on the next request, and needs no deploy.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" onClick={load} disabled={saving}>Reload</Button>
          <Button onClick={save} disabled={!dirty || saving} data-testid="save-role-config">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save changes
          </Button>
        </div>
      </div>

      <Card className="border-blue-500/30 bg-blue-500/5">
        <CardContent className="p-4 flex gap-3">
          <Info className="h-5 w-5 text-blue-500 mt-0.5 shrink-0" />
          <div className="text-sm space-y-1">
            <p>
              These are enforced on the API, not only hidden in the interface. Taking a
              permission away stops the action, it does not merely remove the button.
            </p>
            <p className="text-muted-foreground">
              Changes apply to everyone holding that role, on every advertiser account.
              Existing sessions pick them up on their next request; nobody needs to sign
              in again.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg">The matrix</CardTitle>
          <CardDescription>
            A ticked box is an allowed action. Owner is fixed and cannot be reduced, so
            an account can never be left with nobody able to administer it.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="p-3 text-left text-xs text-muted-foreground uppercase tracking-wider w-[22%]">
                    Role
                  </th>
                  {permissions.map((p) => (
                    <th
                      key={p.key}
                      className="p-3 text-center text-xs text-muted-foreground font-medium align-bottom"
                      title={p.help}
                    >
                      {p.label}
                    </th>
                  ))}
                  <th className="p-3 w-16" />
                </tr>
              </thead>
              <tbody>
                {roles.map((role) => {
                  const isFixed = fixed.has(role);
                  const held = new Set(draft[role] || []);
                  const atDefault =
                    (draft[role] || []).join() === (data.defaults?.[role] || []).join();
                  return (
                    <tr key={role} className="border-b border-border last:border-0 align-top">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{data.role_labels?.[role] || role}</span>
                          {isFixed && (
                            <Badge variant="outline" className="gap-1 text-[10px]">
                              <Lock className="h-3 w-3" />
                              Fixed
                            </Badge>
                          )}
                          {!isFixed && !atDefault && (
                            <Badge variant="secondary" className="text-[10px]">Custom</Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 leading-snug">
                          {isFixed
                            ? "The account owner always holds every permission, so no account can be left without an administrator."
                            : data.role_descriptions?.[role]}
                        </p>
                      </td>
                      {permissions.map((p) => (
                        <td key={p.key} className="p-3 text-center">
                          <Checkbox
                            checked={held.has(p.key)}
                            disabled={isFixed}
                            onCheckedChange={() => toggle(role, p.key)}
                            aria-label={`${role} ${p.label}`}
                            data-testid={`perm-${role}-${p.key}`}
                          />
                        </td>
                      ))}
                      <td className="p-3 text-right">
                        {!isFixed && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => resetRole(role)}
                            disabled={atDefault}
                            title="Back to the shipped default for this role"
                            data-testid={`reset-${role}`}
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">What each permission covers</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-2 md:grid-cols-2">
          {permissions.map((p) => (
            <div key={p.key} className="text-sm">
              <span className="font-medium">{p.label}</span>
              <span className="text-muted-foreground"> {p.help}</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
